import { useEffect, useState } from 'react';
import { isBelowMinimum, limitToCents } from '../../lib/amount';
import Modal from '../Modal';
import portfolioService, { type ProductSummary } from '../../services/portfolio.service';
import proposalsService from '../../services/proposals.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatMoney } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import type { ProposalCadence } from '../../types/proposals';

/**
 * Propose a product to a client (0070) — the upselling action after a call.
 * The server refuses a product that is not suitable for the client, a client
 * off the agent's roster, or a second open proposal for the same product, and
 * this modal shows that answer as it comes back.
 */

interface Props {
  clientId: string;
  clientName: string;
  onClose: () => void;
  onCreated: () => void;
}

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${colors.border}`,
  background: colors.surface,
  color: colors.ink,
  fontSize: 14,
};

function Label({ text, children }: Readonly<{ text: string; children: React.ReactNode }>): JSX.Element {
  return (
    <label className="block text-xs font-semibold mb-3" style={{ color: colors.muted }}>
      {text}
      <div className="mt-1">{children}</div>
    </label>
  );
}

export default function ProposeProductModal({
  clientId,
  clientName,
  onClose,
  onCreated,
}: Readonly<Props>): JSX.Element {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [productId, setProductId] = useState('');
  const [amount, setAmount] = useState('');
  const [cadence, setCadence] = useState<ProposalCadence | ''>('');
  const [message, setMessage] = useState('');
  const [days, setDays] = useState('30');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    portfolioService.listProducts()
      .then((rows) => {
        if (cancelled) return;
        // /portfolio/products already excludes the synthetic onboarding host
        // server-side (it has no funds vehicle), so every row here is offerable.
        setProducts(rows);
        setProductId((current) => current || rows[0]?.id || '');
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(getApiErrorMessage(e, 'Could not load the Series.'));
      });
    return () => { cancelled = true; };
  }, []);

  const product = products.find((p) => p.id === productId);
  const belowMinimum = amount.trim() !== '' && isBelowMinimum(amount, product?.min_investment);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!productId) return;
    setSubmitting(true);
    setError(null);
    try {
      await proposalsService.create(clientId, {
        product_id: productId,
        suggested_amount: amount.trim() || null,
        suggested_cadence: cadence || null,
        message: message.trim() || null,
        expires_in_days: Number.parseInt(days, 10) || 30,
      });
      onCreated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not send the proposal.'));
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} maxWidth="lg" busy={submitting}>
      <form onSubmit={(e) => { void submit(e); }} className="p-5">
        <h2 className="text-base font-semibold mb-1" style={{ color: colors.ink }}>
          Propose a Series
        </h2>
        <p className="text-xs mb-4" style={{ color: colors.muted }}>
          {clientName} will see it on their dashboard and can review and sign it on their own.
          Only Series suitable for this client can be proposed.
        </p>
        <Label text="Series">
          <select value={productId} onChange={(e) => setProductId(e.target.value)} style={fieldStyle}>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </Label>
        <div className="grid grid-cols-2 gap-3">
          <Label text="Suggested amount (optional)">
            <input
              type="number"
              min="0.01"
              step="0.01"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(limitToCents(e.target.value))}
              style={fieldStyle}
            />
            {product?.currency_code && Number(product.min_investment ?? 0) > 0 && (
              <span
                className="block text-xs mt-1"
                style={{ color: belowMinimum ? colors.warning : colors.muted }}
              >
                {`Minimum first contribution: ${formatMoney(product.min_investment, product.currency_code)}`}
                {belowMinimum && (
                  <span className="block">Below it, only a client who already holds this series can add it.</span>
                )}
              </span>
            )}
          </Label>
          <Label text="Suggested schedule (optional)">
            <select
              value={cadence}
              onChange={(e) => setCadence(e.target.value as ProposalCadence | '')}
              style={fieldStyle}
            >
              <option value="">None</option>
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="one_time">One time</option>
            </select>
          </Label>
        </div>
        <Label text="Message to the client (optional)">
          <textarea
            value={message}
            maxLength={1000}
            rows={3}
            onChange={(e) => setMessage(e.target.value)}
            style={fieldStyle}
          />
        </Label>
        <Label text="Open for (days)">
          <input
            type="number"
            min={1}
            max={90}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            style={fieldStyle}
          />
        </Label>
        {error && (
          <p className="text-sm mb-3" role="alert" style={{ color: colors.danger }}>
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm"
            style={{ color: colors.inkSoft, border: `1px solid ${colors.border}` }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !productId}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: colors.ink, color: colors.surface }}
          >
            {submitting ? 'Sending…' : 'Send proposal'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
